import {
  Box,
  Button,
  CartAddIcon,
  constants,
  Modal,
  ModalBody,
  Stack,
  Text,
} from '@redbubble/design-system';
import { defineMessages, IntlShape, useIntl } from '@redbubble/next/intl';
import Image from 'next/image';

const messages = defineMessages({
  modalTitle: {
    defaultMessage: 'Error adding item to cart',
  },
  transientTitle: {
    defaultMessage: 'Error - item not added to cart',
  },
  transientDescription: {
    defaultMessage:
      'Due to a temporary system error the item wasn’t added to your cart. Please try again.',
  },
  retryATC: {
    defaultMessage: 'Retry add to cart',
  },
  continueShopping: {
    defaultMessage: 'Continue shopping',
  },
  errorAlt: {
    defaultMessage: 'Error adding item to cart',
  },
});

interface BaseProps {
  imageSrc: string;
  open: boolean;
  onClose: () => void;
}

type RetryableModal = {
  type: 'retryable';
  onRetry: () => void;
};

// TODO[PLC-2166] - add product unavailable variant
export type ModalVariants = RetryableModal;
export type ErrorModalProps = BaseProps & ModalVariants;

interface ModalVariantConfig {
  imageAlt: string;
  title: string;
  description: string;
  primaryActionText: string;
  primaryAction: () => void;
  secondaryActionText?: string;
  secondaryAction?: () => void;
  iconBefore?: React.ReactNode;
}

const getConfig = (
  variant: ModalVariants,
  onClose: () => void,
  intl: IntlShape
): ModalVariantConfig | null => {
  switch (variant.type) {
    case 'retryable':
      return {
        imageAlt: intl.formatMessage(messages.errorAlt),
        title: intl.formatMessage(messages.transientTitle),
        description: intl.formatMessage(messages.transientDescription),
        primaryActionText: intl.formatMessage(messages.retryATC),
        primaryAction: variant.onRetry,
        secondaryActionText: intl.formatMessage(messages.continueShopping),
        secondaryAction: onClose,
        iconBefore: <CartAddIcon />,
      };
    default:
      return null;
  }
};

export const ErrorModal = (props: ErrorModalProps) => {
  const intl = useIntl();
  const { imageSrc, open, onClose, ...variantProps } = props;
  const variantConfig = getConfig(variantProps, onClose, intl);

  if (!variantConfig) return;

  return (
    <Modal
      open={open}
      onCloseRequested={onClose}
      accessibleTitle={intl.formatMessage(messages.modalTitle)}
      data-testid="error-modal"
    >
      <ModalBody>
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          textAlign="center"
        >
          <Stack direction={constants.VERTICAL} spacing="m" paddingTop="s">
            <Image
              alt={variantConfig.imageAlt}
              src={imageSrc}
              height={140}
              width={140}
              style={{ objectFit: 'contain' }}
            />
            <Text type="display3">{variantConfig.title}</Text>
            <Text type="body">{variantConfig.description}</Text>
          </Stack>
          <Stack direction={constants.VERTICAL} spacing="s" paddingTop="xl">
            <Button
              onClick={variantConfig.primaryAction}
              size={constants.LARGE}
              strong
              intent={constants.PRIMARY}
              fluid
              iconBefore={variantConfig.iconBefore}
            >
              {variantConfig.primaryActionText}
            </Button>
            {variantConfig.secondaryActionText && (
              <Button
                size={constants.LARGE}
                fluid
                onClick={variantConfig.secondaryAction}
              >
                {intl.formatMessage(messages.continueShopping)}
              </Button>
            )}
          </Stack>
        </Box>
      </ModalBody>
    </Modal>
  );
};
